import * as React from 'react';
import Popup from "./Popup";
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box,
    Button, Chip,
    CircularProgress,
    DialogActions, FormControl, FormControlLabel, FormGroup, FormHelperText,
    Grid, List, ListItem, ListItemButton, ListItemText, Paper, Switch, TextField,
    Typography
} from "@mui/material";
import {useContext, useState} from "react";
import { red } from '@mui/material/colors';
import API, {HandleError} from "../API";
import AppContext from "../AppContext";
import {useSnackbar} from "notistack";
import {ExpandMore} from "@mui/icons-material";
import {fdate, mysql_time} from "../Common";
import {DesktopTimePicker} from "@mui/lab";

export default function CourseBulk({ popup, setPopup, table, setTable, children }) {
    const { enqueueSnackbar } = useSnackbar();
    const app = useContext(AppContext);

    const [form, setForm] = useState({
        fields: {
            cancelled: 0,
            active: 0,
            start_time: null,
            end_time: null
        },
        sections: []
    })

    const onClose = () => {
        setPopup({...popup, open: false, items: [], title: '', loading: false });
        setForm({
            fields: {
                cancelled: 0,
                active: 0,
                start_time: null,
                end_time: null
            },
            sections: []
        })
    }

    const onConfirm = async () => {
        setPopup({...popup, loading: true, title: `Updating ${popup.items?.length} courses...`})
        try {
            let ids = popup.items.map((item, idx) => item.ID);
            let fields = {...form.fields}
            for(var k in fields) {
                if(k === 'start_time' || k === 'end_time') {
                    if(form.sections.indexOf('times') === -1) {
                        delete fields['start_time'];
                        delete fields['end_time'];
                    }
                    else {
                        fields[k] = fields[k] ? mysql_time(fields[k]) : null;
                    }
                }
                else {
                    if(form.sections.indexOf(k) === -1)
                        delete fields[k];
                }
            }
            const res = await API({
                method: 'PUT', url: `/courses?lock=1`,
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                },
                data: {
                    ids: ids,
                    fields: fields
                }
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            const updated = res.data.updated;
            const failed = res.data.failed;
            if(failed.length) {
                let msg = [`Couldn't update ${failed.length} / ${ids.length} courses:`]
                failed.forEach((err, idx) => {
                    if(err.message)
                        msg.push(`#${err.ID}: ${err.message}`);
                })
                enqueueSnackbar(msg.join("\n"), {
                    variant: 'error',
                    style: { whiteSpace: 'pre-line' }
                });
            }
            if(!updated.length) {
                if(!failed.length)
                    enqueueSnackbar(`No courses were updated.`, { variant: 'error' });
            }
            else {
                const newResults = [...table.results]
                if(!failed.length) {
                    enqueueSnackbar(`Updated ${updated.length} course(s).`, {variant: 'success'});
                }
                else {
                    let msg = [`Updated ${updated.length} / ${ids.length} courses:`]
                    msg.push(updated.map((item, idx) => item.ID).join(', '));
                    enqueueSnackbar(msg.join("\n"), {
                        variant: 'warning',
                        style: {whiteSpace: 'pre-line'}
                    });
                }
                updated.forEach((item, idx) => {
                    //update visible row in table.
                    for (var i = 0; i < newResults.length; i++) {
                        if (newResults[i].ID === item.ID) {
                            newResults[i] = item;
                        }
                    }
                })
                setTable({...table, results: newResults})
            }
            setPopup({...popup, loading: false })
        }
        catch(e) {
            setPopup({...popup, loading: false, title: `Update Courses`})
            HandleError(e, 'Update Courses', enqueueSnackbar, app.logout);
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
    }

    const handleSwitch = (e) => {
        let newForm = {...form}
        newForm.fields[e.target.name] = e.target.checked ? 1 : 0;
        setForm(newForm);
    }

    const handleDateChange = (field, newDate) => {
        let newForm = {...form}
        newForm.fields[field] = newDate;
        setForm(newForm);
    }

    const enabled = (section) => {
        return form.sections.indexOf(section) !== -1
    }

    const toggleSection = (section) => {
        let sections = [...form.sections];
        const idx = sections.indexOf(section);
        if(idx !== -1) {
            sections.splice(idx, 1);
        }
        else {
            sections.push(section);
        }

        setForm({...form, sections: sections})
        return true;
    }
    return (
        <Popup popup={popup} setPopup={setPopup} onClose={onClose}>
            {popup.loading ? <CircularProgress /> :
                <>
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <Grid container component="main" columnSpacing={6}>
                            <Grid item xs={12} sm={12} md={7} sx={{mb: 2}}>
                                <Typography variant="overline" display="block" >
                                    Toggle sections for bulk-update:
                                </Typography>
                                <Accordion expanded={enabled('cancelled')}
                                           onChange={() => toggleSection('cancelled')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                    >
                                        <Typography sx={{ width: '33%', flexShrink: 0 }}>
                                            Cancelled
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary' }}>Mark/unmark course as cancelled.</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <FormGroup sx={{mt: 3}}>
                                            <FormControlLabel
                                                label="Cancelled"
                                                labelPlacement="end"
                                                control={
                                                    <Switch checked={form.fields.cancelled > 0}  name="cancelled" color="error" onChange={handleSwitch}/>
                                                }
                                            />
                                        </FormGroup>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={enabled('active')}
                                           onChange={() => toggleSection('active')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                    >
                                        <Typography sx={{ width: '33%', flexShrink: 0 }}>
                                            Active
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary' }}>Show/Hide course from front-end.</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <FormGroup sx={{mt: 3}}>
                                            <FormControlLabel
                                                label="Active"
                                                labelPlacement="end"
                                                control={
                                                    <Switch checked={form.fields.active > 0}  name="active" color="primary" onChange={handleSwitch}/>
                                                }
                                            />
                                        </FormGroup>
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion expanded={enabled('times')}
                                           onChange={() => toggleSection('times')}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                    >
                                        <Typography sx={{ width: '33%', flexShrink: 0 }}>
                                            Start / End Times
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary' }}>Override default start/end times.</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container component="main" columnSpacing={1}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormGroup>
                                                    <FormControl sx={{ m: 1, mb:2, minWidth: 140 }}>
                                                        <DesktopTimePicker
                                                            reduceAnimations={true}
                                                            disableOpenPicker={true}
                                                            ampm={false}
                                                            ampmInClock={false}
                                                            label="Start Time"
                                                            name="start_time"
                                                            inputFormat="HH:mm"
                                                            value={form.fields.start_time}
                                                            readOnly={form.loading}
                                                            minutesStep={5}
                                                            clearable
                                                            onChange={(time) => handleDateChange('start_time', time)}
                                                            renderInput={(params) => <TextField {...params} />}
                                                        />
                                                        <FormHelperText>{form.errorFields?.start_time}</FormHelperText>
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <FormGroup>
                                                    <FormControl sx={{ m: 1, mb:2, minWidth: 140 }}>
                                                        <DesktopTimePicker
                                                            reduceAnimations={true}
                                                            disableOpenPicker={true}
                                                            ampm={false}
                                                            ampmInClock={false}
                                                            label="End Time"
                                                            name="end_time"
                                                            inputFormat="HH:mm"
                                                            value={form.fields.end_time}
                                                            readOnly={form.loading}
                                                            minutesStep={5}
                                                            clearable
                                                            onChange={(time) => handleDateChange('end_time', time)}
                                                            renderInput={(params) => <TextField {...params} />}
                                                        />
                                                        <FormHelperText>{form.errorFields?.end_time}</FormHelperText>
                                                    </FormControl>
                                                </FormGroup>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                            <Grid item xs={12} sm={12} md={5}>
                                <Typography variant="overline" display="block" >
                                    {popup.items.length > 1 ? `${popup.items.length} courses will be updated:` : `Are you sure you want to update this course?`}
                                </Typography>
                                <Paper>
                                    <List round="true" sx={{ width: '100%', maxHeight: 200, overflow: 'auto', bgcolor: 'background.paper'}}>
                                        {popup.items?.map((item, idx) => {
                                            return (
                                                <ListItem
                                                    key={idx}
                                                    disablePadding
                                                >
                                                    <ListItemButton>
                                                        <ListItemText primary={`${item.product} / ${fdate(item.date)} in ${item.location}`} />
                                                        <Chip label={`#${item.ID}`} variant="outlined" />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    {children}
                    <DialogActions>
                        <Box sx={{ m: 1, position: 'relative' }}>
                            <Button onClick={onConfirm} disabled={popup.loading}>Confirm</Button>
                            {popup.loading && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        color: red[400],
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                    }}
                                />
                            )}
                        </Box>
                        <Button onClick={onClose} disabled={popup.loading} sx={{color: (theme) => theme.palette.grey[700]}}>Close</Button>
                    </DialogActions>
                </>}
        </Popup>
    )
}